import { z } from 'zod';

import { apiGet, apiPatch, apiPost } from '../client';
import {
  BookingSchema,
  BusinessTableListResponseSchema,
  BusinessTableResponseSchema,
  type Booking,
  type BusinessTable,
} from '../schemas';

const BookingResponseSchema = z.object({ data: BookingSchema });

/** Owner-only floor plan of Mesas (docs/CONCEPTS.md). */
export function listTables(includeInactive: boolean): Promise<{ data: BusinessTable[] }> {
  const query = includeInactive ? '?includeInactive=true' : '';
  return apiGet(`/v1/business/tables${query}`, BusinessTableListResponseSchema);
}

export type CreateTableInput = {
  name: string;
  seats: number;
  kind?: string;
  isPrivate?: boolean;
  zone?: string;
  gridX?: number;
  gridY?: number;
  gridW?: number;
  gridH?: number;
  shape?: 'circle' | 'square' | 'rectangle';
  rotation?: number;
};

export async function createTable(
  input: CreateTableInput,
  idempotencyKey?: string,
): Promise<BusinessTable> {
  const response = await apiPost(
    '/v1/business/tables',
    BusinessTableResponseSchema,
    input,
    idempotencyKey,
  );
  return response.data;
}

export type PatchTableInput = Partial<{
  name: string;
  seats: number;
  kind: string;
  isPrivate: boolean;
  zone: string;
  gridX: number;
  gridY: number;
  gridW: number;
  gridH: number;
  shape: 'circle' | 'square' | 'rectangle';
  rotation: number;
  isActive: boolean;
  /** The version the form read; a stale one comes back 409 version_conflict. */
  expectedVersion: number;
}>;

export async function patchTable(
  tableId: string,
  patch: PatchTableInput,
  idempotencyKey: string,
): Promise<BusinessTable> {
  const response = await apiPatch(
    `/v1/business/tables/${encodeURIComponent(tableId)}`,
    BusinessTableResponseSchema,
    patch,
    idempotencyKey,
  );
  return response.data;
}

/** Moves a live Reserva onto a different mesa; 409 with the shared 'ocupada' message on any conflict. */
export async function reassignBookingTable(
  bookingId: string,
  tableId: string,
  idempotencyKey: string,
): Promise<Booking> {
  const response = await apiPatch(
    `/v1/business/bookings/${encodeURIComponent(bookingId)}/table`,
    BookingResponseSchema,
    { tableId },
    idempotencyKey,
  );
  return response.data;
}
